/**
 * /audio-overlay — связка: интерфейс, жесты, воспроизведение, выдача.
 *
 * Модули рядом отвечают каждый за своё и ничего не знают про DOM:
 *   model.js     — что лежит в документе и КОГДА это звучит (чистая арифметика);
 *   mixer.js     — как из этого собираются узлы Web Audio (предпросмотр = экспорт);
 *   analysis.js  — пики, паузы, громкость;
 *   timeline.js  — рисование шкалы и жесты по ней;
 *   export.js    — потоковая выдача.
 *
 * Здесь — только склейка и состояние экрана.
 */

import {
    MODE_INSERT, MODE_OVERLAY, LIMITS, makeModel, newClip, plan, clipLength,
    makeHistory, rememberAsset, mapToResult, mapToOriginal,
} from './model.js';
import { schedule, outputChannels } from './mixer.js';
import { buildPeaks, findPauses, snapToPause, matchGain, loudnessOf } from './analysis.js';
import { createTimeline, fmtTime } from './timeline.js';
import { exportResult } from './export.js';

// window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
// shown changes. A page built before they existed can still be handed this file — it then writes the
// plain way. Why: docs/why/primitives/per-pixel-writes.md.
function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }


const CFG = window.__AO || { slug: 'audio-overlay', lang: {}, batchCap: 1 };
const L = CFG.lang || {};
const SLUG = CFG.slug || 'audio-overlay';

const $ = (id) => document.getElementById(id);
const ae = (name, data) => { try { window.__ae && window.__ae(name, data || {}); } catch (_) {} };
const toast = (m, kind) => { try { window.__toast && window.__toast(m, kind || 'info'); } catch (_) {} };

/* ── состояние ───────────────────────────────────────────────────────── */
const model = makeModel();
const history = makeHistory(model);
let planned = null;
let timeline = null;
/* Режим добавления по умолчанию — НАЛОЖИТЬ. Прежде им была вставка, а люди
   приходят «наложить аудио на аудио» (Вордстат: 2511 против 1244 у «вставить»).
   RU, 20 дней до 30.10.2026: первый клип ушёл вставкой в 2087 сессиях из 3070
   (кнопка «Выбрать файл» на телефоне — только по умолчанию: бросать там некуда);
   кто так и остался на вставке, доводил до файла 23.8 %, кто переключил на
   наложение — 60.9 %, кто сразу наложил — 56.3 %; в готовых файлах наложение
   у 1285 сессий, одни вставки — у 232. Выбор человека помнится (`add_mode`). */
let addMode = MODE_OVERLAY;
let addModeHow = 'd';   // как решён режим: d — умолчание, r — запомнен, c — выбран здесь
let pauses = [];
let selectedId = null;
let audioCtx = null;
let playing = false;
let playSources = [];
let playFrom = 0;
let playStartedAt = 0;
let rafId = 0;
let exportAbort = null;
let batchFiles = [];

/* ── разметка времени ────────────────────────────────────────────────── */
function fmtMs(sec) {
    // До десятых — раньше минут: 59.97 с это «1:00.0», а не «0:60.0».
    const s = Math.round(Math.max(0, sec || 0) * 10) / 10;
    const m = Math.floor(s / 60);
    const rest = Math.round((s - m * 60) * 10) / 10;
    return m + ':' + (rest < 10 ? '0' : '') + rest.toFixed(1);
}

/* ── пересчёт и перерисовка ──────────────────────────────────────────── */
let restartTimer = 0;

function replan(opts) {
    planned = plan(model);
    if (timeline) {
        timeline.setModel(model);
        timeline.setPlanned(planned);
    }
    paintNumbers();
    if (playing && !(opts && opts.keepAudio)) scheduleRestart();
}

/**
 * Пересобрать звучащий граф — с придержкой в 140 мс.
 *
 * Картинка отвечает на движение ползунка МГНОВЕННО (она считается из пиков
 * арифметикой), а звук пересобрать на каждый пиксель нельзя: полсотни
 * перезапусков в секунду слышны как треск. 140 мс — это пауза, которой
 * человек не замечает, но которой хватает, чтобы протащить ползунок.
 */
function scheduleRestart() {
    clearTimeout(restartTimer);
    restartTimer = setTimeout(() => { if (playing) restartPlayback(); }, 140);
}

function paintNumbers() {
    if (!model.main) return;
    const src = model.main.buffer.duration;
    const res = planned ? planned.duration : src;
    $('ao-dur-src').textContent = fmtTime(src);
    $('ao-dur-res').textContent = fmtTime(res);
    $('ao-exp-res').textContent = fmtTime(res);
    $('ao-total').textContent = fmtTime(res);
    const d = res - src;
    const el = $('ao-delta');
    el.textContent = Math.abs(d) < 0.05 ? '' : (d > 0 ? '+' : '−') + fmtTime(Math.abs(d));
    $('ao-undo').disabled = !history.canUndo();
    $('ao-redo').disabled = !history.canRedo();
}

/* ── разбор клипа ────────────────────────────────────────────────────── */
function selectClip(id) {
    selectedId = id;
    if (timeline) timeline.setSelected(id);
    const ws = $('ao-ws');
    const clip = id ? model.clips.find((c) => c.id === id) : null;
    $('ao-face-add').classList.toggle('on', !clip);
    $('ao-face-clip').classList.toggle('on', !!clip);
    ws.dataset.clip = clip ? clip.mode : 'none';
    if (clip) fillInspector(clip);
}

function fillInspector(c) {
    $('ao-clip-name').textContent = c.name;
    if (!$('ao-clip-note').dataset.said) $('ao-clip-note').textContent = L.trimHint || '';
    $('ao-clip-dot').classList.toggle('ovl', c.mode === MODE_OVERLAY);
    if ($('ao-at').__timeField) $('ao-at').__timeField.refresh(); else $('ao-at').value = fmtMs(c.at);
    $('ao-len').textContent = fmtMs(clipLength(c));
    $('ao-gain').value = Math.round(c.gain * 100);
    $('ao-gain-v').textContent = Math.round(c.gain * 100) + '%';
    $('ao-join').value = Math.round(c.join * 1000);
    $('ao-join-v').textContent = Math.round(c.join * 1000) + ' ms';
    $('ao-gap-b').value = c.gapBefore;
    $('ao-gap-a').value = c.gapAfter;
    $('ao-fi').value = c.fadeIn;
    $('ao-fo').value = c.fadeOut;
    $('ao-duck').value = c.duck;
    $('ao-duck-v').textContent = c.duck + ' dB';
    document.querySelectorAll('#ao-clip-mode .tc-seg-b').forEach((b) => {
        const on = b.dataset.cmode === c.mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    document.querySelectorAll('.ao-only-ovl').forEach((el) => {
        el.style.display = c.mode === MODE_OVERLAY ? (el.classList.contains('ao-row') ? 'grid' : 'block') : 'none';
    });
    document.querySelectorAll('.ao-only-ins').forEach((el) => {
        el.style.display = c.mode === MODE_INSERT ? (el.classList.contains('ao-row') ? 'grid' : 'block') : 'none';
    });
}

const selected = () => model.clips.find((c) => c.id === selectedId) || null;

function edit(fn, opts) {
    const c = selected();
    if (!c) return;
    if (!(opts && opts.noHistory)) history.push();
    fn(c);
    replan();
    fillInspector(c);
}

/* ── добавление клипов ───────────────────────────────────────────────── */
async function decodeFile(file) {
    const ctx = ensureCtx();
    if (window.AudioDecoderRobust && typeof window.AudioDecoderRobust.robust === 'function') {
        const r = await window.AudioDecoderRobust.robust(file, ctx, { slug: SLUG });
        return r.buffer;
    }
    return await ctx.decodeAudioData(await file.arrayBuffer());
}

async function addClips(files, atResult, mode, how) {
    if (!model.main) return;
    const segs = planned ? planned.segments : [];
    let at = mapToOriginal(Math.max(0, atResult), segs);
    if ($('ao-snap').checked && pauses.length) {
        const snap = snapToPause(pauses, at, snapWindow());
        if (snap != null) { at = snap; toast(L.snapped || '', 'success'); ae('feature_use', { slug: SLUG, f: 'snap_to_pause' }); }
    }

    for (const file of files) {
        let buffer;
        try {
            buffer = await decodeFile(file);
        } catch (err) {
            toast((L.decodeFail || 'decode failed') + ': ' + (file.name || ''), 'error');
            ae('tool_error', { slug: SLUG, error_type: 'clip_decode_failed' });
            continue;
        }
        if (!buffer || buffer.duration < 0.02) { toast(L.tooShort || '', 'warning'); continue; }
        history.push();
        const c = newClip(mode, at, file, buffer, file.name);
        model.clips.push(c);
        rememberAsset(model, c);
        timeline.setPeaks(c.id, buildPeaks(buffer));
        replan();
        selectClip(c.id);
        at += 0.001;  // несколько файлов подряд не слипаются в одну точку
        // am — откуда режим: умолчание (d), память (r), выбор на странице (c), дорожка броска (lane).
        ae('feature_use', { slug: SLUG, f: 'clip_added', mode, dur: Math.round(buffer.duration), am: how || addModeHow });
        idle(() => loudnessOf(buffer));   // «Выровнять громкость» возьмёт готовое число
    }
}

/* Фоновая работа, которая не должна спорить с жестом человека. */
function idle(fn) {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(() => fn(), { timeout: 2000 });
    else setTimeout(fn, 300);
}

function snapWindow() {
    // Окно притяжения — в ЭКРАННЫХ пикселях, а не в секундах: на обзоре всей
    // записи секунда это доли пикселя, а при увеличении — полэкрана.
    if (!timeline || !planned) return 0.5;
    const perPx = (timeline.xToTime(1) - timeline.xToTime(0)) || 0.01;
    return Math.max(0.15, Math.min(3, perPx * 18));
}

/* ── воспроизведение ─────────────────────────────────────────────────── */
function ensureCtx() {
    if (!audioCtx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtx = new AC();
    }
    return audioCtx;
}

/**
 * Тип аудиосессии (Safari 17+, iOS).
 *
 * Без него iOS считает звук страницы «окружающим»: аппаратный переключатель
 * «без звука» на айфоне глушит предпросмотр целиком, и человек видит идущую
 * иглу при полной тишине — состояние, неотличимое от сломанного инструмента.
 * `playback` говорит системе, что звук и есть смысл страницы.
 * Где возможности нет — ничего не делаем, поведение прежнее.
 */
function claimAudioSession() {
    try {
        if (navigator.audioSession && navigator.audioSession.type !== 'playback') {
            navigator.audioSession.type = 'playback';
        }
    } catch (_) {}
}

async function liveCtx() {
    claimAudioSession();
    const ctx = ensureCtx();
    if (window.__liveAudioCtxReady) {
        const live = await window.__liveAudioCtxReady(ctx);
        // Вернулся ДРУГОЙ объект — прежний мёртв вместе со своими узлами.
        if (live && live !== audioCtx) audioCtx = live;
        return audioCtx;
    }
    if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (_) {} }
    return ctx;
}

function stopSources(list) {
    for (const s of list) { try { s.stop(); } catch (_) {} try { s.disconnect(); } catch (_) {} }
    list.length = 0;
}

/* Жетон старта. Пробуждение устройства (`liveCtx`) на холодной машине длится
   до 2.5 с; «Пауза», уход со вкладки или новый старт за это время должны
   отменить ждущий старт, а не дать ему зазвучать следом. */
let playToken = 0;
let waking = false;   // старт ждёт пробуждения устройства: для кнопки это уже «играет»

async function startPlayback(from) {
    if (!planned || !model.main) return;
    const token = ++playToken;
    waking = true;
    paintPlayButton();
    let ctx;
    try { ctx = await liveCtx(); }
    finally { if (token === playToken) { waking = false; paintPlayButton(); } }
    if (token !== playToken || !planned || !model.main) return;
    stopSources(playSources);
    playFrom = Math.max(0, Math.min(from, planned.duration - 0.02));
    const when = ctx.currentTime + 0.04;
    playSources = schedule(ctx, planned, model.main.buffer, ctx.destination, {
        from: playFrom, to: planned.duration, when,
    });
    playStartedAt = when;
    playing = true;
    paintPlayButton();
    tick();
}

function restartPlayback() {
    const at = currentTime();
    startPlayback(at);
}

/**
 * Откуда играть по нажатию Play (кнопка, пробел).
 *
 * Игла, стоящая в конце результата, — это «дослушали»: Play начинает с
 * начала, как в /podcast-editor и в любом плеере. Прежде отсюда звучали
 * последние 20 мс, кнопка мигала и гасла, и человек видел «звук перестал
 * воспроизводиться» (жалоба 14.10.2026, Edge 155: выпуск 5:40 дослушан до
 * конца — дальше тишина при живом контексте и без единой ошибки).
 */
const END_REWIND_S = 0.05;
function playFromHead() {
    const head = timeline ? (timeline.state.playhead || 0) : 0;
    return planned && head >= planned.duration - END_REWIND_S ? 0 : head;
}

/* Прибор: чем человек запускает звук и сколько раз Play пришлось на конец.
   Нажатия Play телеметрия раньше не видела вовсе — жалобу «звук не играет»
   нечем было разобрать. Одно событие на страницу на пару (дверь, с конца). */
const playSeen = new Set();
function notePlay(via, rewound) {
    const k = via + (rewound ? ':end' : '');
    if (playSeen.has(k)) return;
    playSeen.add(k);
    ae('feature_use', { slug: SLUG, f: 'play', via, from_end: rewound ? 1 : 0 });
}

async function userPlay(via, at) {
    if ((playing || waking) && at == null) { stopPlayback(false); return; }
    const head = timeline ? (timeline.state.playhead || 0) : 0;
    const from = at != null ? at : playFromHead();
    notePlay(via, at == null && from !== head);
    try {
        await startPlayback(from);
    } catch (err) {
        // Отказ устройства примитив уже назвал человеку тостом — второй не нужен.
        if (err && err.__userNotified) return;
        try { window.__handleError && window.__handleError(err, 'audio_overlay_play'); } catch (_) {}
    }
}

function currentTime() {
    if (!playing || !audioCtx) return timeline ? timeline.state.playhead : 0;
    return Math.min(planned.duration, playFrom + Math.max(0, audioCtx.currentTime - playStartedAt));
}

function stopPlayback(resetHead) {
    playToken++;
    waking = false;
    stopSources(playSources);
    playing = false;
    cancelAnimationFrame(rafId);
    paintPlayButton();
    if (resetHead && timeline) { timeline.setPlayhead(0); textWrite($('ao-now'), fmtTime(0)); }
}

function tick() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function loop() {
        if (!playing) return;
        const t = currentTime();
        timeline.setPlayhead(t);
        textWrite($('ao-now'), fmtTime(t));
        if (t >= planned.duration - 0.02) { stopPlayback(false); timeline.setPlayhead(planned.duration); return; }
        rafId = requestAnimationFrame(loop);
    });
}

function paintPlayButton() {
    const on = playing || waking;
    $('ao-ic-play').classList.toggle('hidden', on);
    $('ao-ic-pause').classList.toggle('hidden', !on);
}

/* ── выдача ──────────────────────────────────────────────────────────── */
function fillFormats() {
    const fmtSel = $('ao-format');
    const list = (window.AudioExporter && window.AudioExporter.FORMATS)
        ? window.AudioExporter.FORMATS.filter((f) => f.id !== 'm4r')
        : [{ id: 'mp3', label: 'MP3' }, { id: 'wav', label: 'WAV' }];
    fmtSel.innerHTML = list.map((f) => '<option value="' + f.id + '">' + f.label + '</option>').join('');
    fmtSel.value = 'mp3';
    if (window.__toolSettings) window.__toolSettings.bind(SLUG, fmtSel, 'format', 'mp3');
    fillQuality();
    fmtSel.addEventListener('change', fillQuality);
}

function fillQuality() {
    const fmt = $('ao-format').value;
    const lossless = window.AudioExporter ? window.AudioExporter.isLossless(fmt) : (fmt === 'wav' || fmt === 'flac');
    const wrap = $('ao-quality-f');
    wrap.style.display = lossless ? 'none' : '';
    if (lossless) return;
    const sel = $('ao-quality');
    const prev = sel.value;
    sel.innerHTML = [96, 128, 160, 192, 256, 320]
        .map((k) => '<option value="' + k + '">' + k + ' kbps</option>').join('');
    sel.value = prev && [...sel.options].some((o) => o.value === prev) ? prev : '192';
    // Привязка ОДИН раз: `bind` вешает свой слушатель и подставляет сохранённое
    // значение, поэтому повторный вызов при каждой смене формата откатывал бы
    // только что выбранный битрейт и копил обработчики.
    if (window.__toolSettings && !sel.dataset.bound) {
        sel.dataset.bound = '1';
        window.__toolSettings.bind(SLUG, sel, 'kbps', '192');
    }
    // Потолок не-Премиума ставит общий примитив по data-quality-gate.
    if (window.QualityTier && window.QualityTier.rescan) { try { window.QualityTier.rescan(); } catch (_) {} }
}

function showProgress(on, txt, pct) {
    $('ao-prog').classList.toggle('on', !!on);
    if (txt != null) $('ao-prog-txt').textContent = txt;
    if (pct != null) {
        $('ao-prog-fill').style.width = Math.round(pct * 100) + '%';
        $('ao-prog-pct').textContent = Math.round(pct * 100) + '%';
    }
    $('ao-cancel').style.display = on ? '' : 'none';
    $('ao-export').disabled = !!on;
}

async function doExport() {
    if (!model.main) return;
    if (!model.clips.length) { toast(L.noClips || '', 'warning'); return; }

    const token = window.__toolLimit
        ? window.__toolLimit.begin(SLUG, model.main.limitKey || null)
        : { commit() {}, abort() {} };
    if (!token) return;

    stopPlayback(false);
    exportAbort = new AbortController();
    const fmt = $('ao-format').value;
    const kbps = parseInt($('ao-quality').value, 10) || 192;
    const t0 = performance.now();
    showProgress(true, L.rendering || '', 0);
    try { window.__stage && window.__stage('export_start'); } catch (_) {}

    try {
        const { blob, format } = await exportResult(model, planned, {
            format: fmt, bitrate: kbps,
            sampleRate: model.main.buffer.sampleRate,
            channels: outputChannels(model),
            signal: exportAbort.signal,
            onProgress: (p) => showProgress(true, L.rendering || '', p),
            onStage: (s) => showProgress(true, s === 'encode' ? (L.encoding || '') : (L.rendering || ''), null),
        });
        // Пустой результат — это отказ, а не выдача. Без этой проверки объект
        // без байтов уходил в общую дверь, та честно «сохраняла файл» и
        // рапортовала успех (именно так выглядел дефект с finish(), который
        // отдаёт {blob,…}, а не сам Blob).
        if (!blob || !blob.size) throw Object.assign(new Error('empty-output'), { code: 'EMPTY_OUTPUT' });

        const name = outName(format);
        // Общая дверь может ПРИДЕРЖАТЬ загрузку (второй файл в одном жесте) и
        // честно вернуть false. Списывать сутки за файл, который не доехал,
        // нельзя: кап тратит ВЫДАННЫЙ результат.
        // ⚠️ Вердикт приходит ПРОМИСОМ (`Promise.resolve(false)` на придержании),
        // поэтому его нужно дождаться: сравнение с false без await не сработает
        // никогда, и придержанный файл списывал бы сутки молча.
        const delivered = window.__downloadFile ? await window.__downloadFile(blob, name) : false;
        if (delivered === false) {
            token.abort();
            showProgress(false);
            ae('tool_error', { slug: SLUG, error_type: 'download_held' });
            return;
        }
        token.commit();
        showProgress(false);
        // Про сохранение сообщает САМА общая дверь загрузки — второй тост
        // рядом с первым читается как два разных события.
        // Результат объявляется ОДИН раз — через __aeToolComplete (поля двери:
        // ext, via, size — сливаются в то же событие). Прежде тул слал ещё и
        // голый `tool_complete`, и каждый экспорт считался дважды (30 дней:
        // 3715 «готово» на 1875 скачиваний).
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                format, clips: model.clips.length,
                inserts: model.clips.filter((c) => c.mode === MODE_INSERT).length,
                overlays: model.clips.filter((c) => c.mode === MODE_OVERLAY).length,
                ducked: model.clips.filter((c) => c.duck > 0).length,
                out_s: Math.round(planned.duration), ms: Math.round(performance.now() - t0),
            });
        } catch (_) {}
    } catch (err) {
        token.abort();
        showProgress(false);
        if (err && (err.code === 'ABORTED' || err.name === 'AbortError')) { toast(L.cancelled || '', 'info'); return; }
        toast(L.exportFail || 'export failed', 'error');
        try { window.__handleError && window.__handleError(err, SLUG); } catch (_) {}
        ae('tool_error', { slug: SLUG, error_type: 'export_failed', msg: String(err && err.message || err).slice(0, 120) });
    } finally {
        exportAbort = null;
    }
}

function outName(format) {
    const base = (model.main.name || 'audio').replace(/\.[^.]+$/, '');
    // join(base, ext) — ровно два аргумента; имя составляется ДО него.
    if (window.__outputName && window.__outputName.join) {
        const nm = window.__outputName.join(base + '-overlay', format);
        if (nm) return nm;
    }
    return base.replace(/[\/:*?"<>|]+/g, '_') + '-overlay.' + format;
}

/* ── пакет (перк Премиума) ───────────────────────────────────────────── */
function paintBatch() {
    const cap = CFG.batchCap || 1;
    const sub = $('ao-batch-sub');
    const premium = cap > 1;
    sub.textContent = premium ? String(cap) : (L.batchPremium || '');
    $('ao-batch-pick').disabled = !premium;
    $('ao-batch-buy').style.display = premium ? 'none' : '';
    $('ao-batch-run').disabled = !premium || !batchFiles.length || !model.clips.length;

    const list = $('ao-batch-list');
    list.innerHTML = batchFiles.map((f) => (
        '<div class="ao-batch-row"><span>' + escapeHtml(f.name) + '</span><b>' + fmtSize(f.size) + '</b></div>'
    )).join('');

    // Якорь на КАЖДЫЙ клип: «интро» считается от начала, «аутро» — от конца,
    // и без этого выбора пакет по абсолютной секунде осмыслен только для интро.
    const anchor = $('ao-anchor');
    anchor.innerHTML = model.clips.map((c) => (
        '<div class="ao-anchor-row"><span>' + escapeHtml(c.name) + '</span>' +
        '<select class="tc-in" data-anchor-for="' + c.id + '">' +
        '<option value="start"' + (c.anchor !== 'end' ? ' selected' : '') + '>' + escapeHtml(L.anchorStart || 'start') + '</option>' +
        '<option value="end"' + (c.anchor === 'end' ? ' selected' : '') + '>' + escapeHtml(L.anchorEnd || 'end') + '</option>' +
        '</select></div>'
    )).join('');
    anchor.querySelectorAll('select[data-anchor-for]').forEach((sel) => {
        sel.addEventListener('change', () => {
            const c = model.clips.find((x) => x.id === sel.dataset.anchorFor);
            if (c) c.anchor = sel.value;
        });
    });
}

async function runBatch() {
    const cap = CFG.batchCap || 1;
    if (cap <= 1 || !batchFiles.length) return;
    const files = batchFiles.slice(0, cap);
    const keys = window.__toolLimit ? window.__toolLimit.fileKeys(files) : null;
    const token = window.__toolLimit ? window.__toolLimit.begin(SLUG, keys) : { commit() {}, abort() {} };
    if (!token) return;

    exportAbort = new AbortController();
    const fmt = $('ao-format').value;
    const kbps = parseInt($('ao-quality').value, 10) || 192;
    const mainDur = model.main.buffer.duration;
    const saved = model.main;
    let done = 0;
    showProgress(true, L.batchRunning || '', 0);

    // ⚠️ Пакет выдаётся ТОЛЬКО через общую пакетную дверь (папка → архив →
    // честный отказ). Цикл `__downloadFile` по файлам здесь недопустим: браузер
    // отдаёт ОДИН файл на пользовательский жест, а лишний ОТРАВЛЯЕТ ВКЛАДКУ —
    // дальше молча теряются даже одиночные загрузки, в том числе на других
    // страницах того же origin (замер в CLAUDE.md: 476 сессий, 4674 файла).
    // Двери нет — говорим об этом и не делаем вид, что обработали.
    if (!window.__batchDeliver && window.__ensureBatchDeliver) {
        try { await window.__ensureBatchDeliver(); } catch (_) {}
    }
    if (!window.__batchDeliver) {
        token.abort();
        showProgress(false);
        toast(L.batchNoDelivery || '', 'warning');
        ae('tool_error', { slug: SLUG, error_type: 'batch_no_delivery' });
        exportAbort = null;
        return;
    }
    const delivery = await window.__batchDeliver.openBatch({ slug: SLUG, total: files.length });
    try {
        for (const file of files) {
            if (exportAbort.signal.aborted) break;
            let buffer;
            try { buffer = await decodeFile(file); } catch (_) { continue; }
            model.main = { file, buffer, name: file.name };
            // Якоря: клип «от конца» держит расстояние до финала записи.
            const shifted = model.clips.map((c) => {
                const copy = Object.assign({}, c);
                if (c.anchor === 'end') copy.at = Math.max(0, buffer.duration - (mainDur - c.at));
                else copy.at = Math.min(c.at, buffer.duration);
                return copy;
            });
            const sub = { main: model.main, clips: shifted, assets: model.assets };
            const p = plan(sub);
            const { blob, format } = await exportResult(sub, p, {
                format: fmt, bitrate: kbps,
                sampleRate: buffer.sampleRate, channels: outputChannels(sub),
                signal: exportAbort.signal,
                onProgress: (x) => showProgress(true, L.batchRunning || '', (done + x) / files.length),
            });
            const nm = (file.name || 'audio').replace(/\.[^.]+$/, '') + '-overlay.' + format;
            await delivery.write(nm, blob);
            done++;
            showProgress(true, L.batchRunning || '', done / files.length);
        }
        await delivery.finish();
        token.commit();
        showProgress(false);
        toast((L.batchDone || '') + ' ' + done, 'success');
        try { window.__aeToolComplete && window.__aeToolComplete({ batch: 1, files: done, format: fmt }); } catch (_) {}
    } catch (err) {
        token.abort();
        try { if (delivery && delivery.abort) await delivery.abort(); } catch (_) {}
        showProgress(false);
        toast(L.exportFail || '', 'error');
        ae('tool_error', { slug: SLUG, error_type: 'batch_failed', msg: String(err && err.message || err).slice(0, 120) });
    } finally {
        model.main = saved;
        exportAbort = null;
        replan();
    }
}

function escapeHtml(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
function fmtSize(b) { return b > 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.round(b / 1024) + ' KB'; }

/* ── запуск ──────────────────────────────────────────────────────────── */
function boot() {
    const canvas = $('ao-canvas');
    if (!canvas) return;

    timeline = createTimeline({
        canvas,
        laneNames: { main: L.laneMain || '', over: L.laneOver || '' },
        onSeek(t) {
            textWrite($('ao-now'), fmtTime(t));
            if (playing) startPlayback(t);
        },
        onSelect(id) { selectClip(id); },
        onMoveStart() { history.push(); },
        onMove(clip, wantResultTime, noSnap) {
            // Карта считается БЕЗ перетаскиваемого клипа — «вынули и смотрим,
            // куда показывает курсор на оставшейся ленте».
            //
            // ⚠️ Иначе вставку невозможно сдвинуть вправо меньше, чем на её
            // собственную длину: курсор попадает внутрь неё самой, а точка
            // внутри вставки принадлежит месту разреза, и клип упрямо
            // возвращался на прежнюю секунду. Замер: клип на 1.0 с длиной 3 с,
            // курсор на 1.85 с → mapToOriginal вернул ровно 1.0.
            const rest = { main: model.main, clips: model.clips.filter((c) => c.id !== clip.id), assets: model.assets };
            const segs = plan(rest).segments;
            let at = mapToOriginal(Math.max(0, wantResultTime), segs);
            let hint = null;
            if (!noSnap && $('ao-snap').checked && pauses.length) {
                const s = snapToPause(pauses, at, snapWindow());
                if (s != null) { at = s; hint = mapToResult(s, segs, planned.duration); }
            }
            clip.at = Math.max(0, Math.min(at, model.main.buffer.duration));
            timeline.setSnapHint(hint);
            replan({ keepAudio: true });
            fillInspector(clip);
        },
        /**
         * Обрезка клипа за край прямо на шкале.
         *
         * Правило как в монтажных программах: КРАЙ, который тянут, идёт за
         * курсором, противоположный стоит на месте. Для левого края это значит
         * не только «отрезать голову» (trimIn), но и сдвинуть точку на ту же
         * величину — иначе блок сжимался бы справа, хотя человек тянет слева.
         */
        onTrim(clip, side, localT) {
            const MIN = 0.05;
            const full = clip.buffer ? clip.buffer.duration : 0;
            if (side === 'out') {
                clip.trimOut = Math.max(clip.trimIn + MIN, Math.min(full, clip.trimIn + localT));
            } else {
                const delta = Math.max(-clip.trimIn, Math.min(localT, clipLength(clip) - MIN));
                clip.trimIn += delta;
                clip.at = Math.max(0, Math.min(clip.at + delta, model.main.buffer.duration));
            }
            replan({ keepAudio: true });
            fillInspector(clip);
        },
        onMoveEnd(clip, moved) {
            // Снимок уже взят в onMoveStart. Жест без движения (просто клик по
            // клипу) снимок не заслужил — снимаем его обратно.
            if (moved) { paintNumbers(); if (playing) restartPlayback(); }
            else history.undo();
        },
        onDropFiles(files, atResult, mode) { addClips(files, atResult, mode, 'lane'); },
        onZoom(f) { $('ao-zoomv').textContent = (f < 10 ? f.toFixed(1) : Math.round(f)) + '×'; },
    });

    /* зона загрузки */
    const zone = $('ao-upload');
    zone.addEventListener('tool-uploader:decoded', (e) => {
        const buf = e.detail && e.detail.buffer;
        if (!buf) return;
        const file = e.detail.file || null;
        model.main = { file, buffer: buf, name: (file && file.name) || 'audio' };
        // Ключ капа штампуется ОДИН раз при загрузке: если вывести его из живого
        // File на каждое нажатие, повторная выдача того же файла перестанет быть
        // бесплатной, как только тул хоть раз подменит файл (разбор в CLAUDE.md).
        if (window.__toolLimit && file) model.main.limitKey = window.__toolLimit.fileKey(file);
        model.clips = [];
        history.reset();
        selectClip(null);

        const peaks = buildPeaks(buf);
        timeline.setPeaks('main', peaks);
        pauses = findPauses(peaks);
        timeline.setPauses(pauses);
        $('ao-pause-note').textContent = pauses.length
            ? (L.pauseFound || '').replace(':n', String(pauses.length))
            : (L.pauseNone || '');

        $('ao-ws').classList.add('active');
        requestAnimationFrame(() => { timeline.resize(); replan(); });
        idle(() => loudnessOf(buf));   // громкость записи — заранее, порциями (см. analysis.js)
        ae('feature_use', {
            slug: SLUG, f: 'main_loaded', dur: Math.round(buf.duration),
            tier: e.detail.tier, pauses: pauses.length,
        });
        try { window.__stage && window.__stage('file_loaded'); } catch (_) {}
    });
    zone.addEventListener('tool-uploader:clear', () => {
        stopPlayback(true);
        model.main = null; model.clips = []; history.reset();
        planned = null; pauses = [];
        timeline.setPauses([]); timeline.setPlanned(null);
        $('ao-ws').classList.remove('active');
        selectClip(null);
    });

    /* режим добавления: умолчание — «Наложить» (почему — у `addMode` выше),
       выбор человека помнится между заходами */
    const paintAddMode = () => {
        document.querySelectorAll('#ao-face-add .tc-seg-b').forEach((x) => {
            const on = (x.dataset.mode === 'overlay') === (addMode === MODE_OVERLAY);
            x.classList.toggle('on', on);
            x.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        $('ao-mode-hint').textContent = addMode === MODE_OVERLAY ? (L.hintOverlay || '') : (L.hintInsert || '');
    };
    if (window.__toolSettings) {
        const saved = window.__toolSettings.load(SLUG, 'add_mode', '');
        if (saved === 'insert' || saved === 'overlay') {
            addMode = saved === 'insert' ? MODE_INSERT : MODE_OVERLAY;
            addModeHow = 'r';
        }
    }
    paintAddMode();
    document.querySelectorAll('#ao-face-add .tc-seg-b').forEach((b) => {
        b.addEventListener('click', () => {
            addMode = b.dataset.mode === 'overlay' ? MODE_OVERLAY : MODE_INSERT;
            addModeHow = 'c';
            paintAddMode();
            if (window.__toolSettings) window.__toolSettings.save(SLUG, 'add_mode', addMode === MODE_OVERLAY ? 'overlay' : 'insert');
        });
    });

    /* выбор файла клипа */
    const clipInput = $('ao-clip-input');
    const pick = () => clipInput.click();
    $('ao-pick').addEventListener('click', pick);
    $('ao-add-here').addEventListener('click', pick);
    clipInput.addEventListener('change', () => {
        const files = Array.from(clipInput.files || []);
        clipInput.value = '';
        if (files.length) addClips(files, currentTime(), addMode, addModeHow);
    });

    /* транспорт */
    $('ao-play').addEventListener('click', () => userPlay('button'));
    $('ao-zoom-in').addEventListener('click', () => timeline.zoomIn());
    $('ao-zoom-out').addEventListener('click', () => timeline.zoomOut());
    $('ao-undo').addEventListener('click', () => { if (history.undo()) { selectClip(null); replan(); } });
    $('ao-redo').addEventListener('click', () => { if (history.redo()) { selectClip(null); replan(); } });

    /* ручки клипа */
    $('ao-gain').addEventListener('input', () => {
        const v = parseInt($('ao-gain').value, 10) / 100;
        edit((c) => { c.gain = Math.max(LIMITS.gain.min, Math.min(LIMITS.gain.max, v)); }, { noHistory: true });
    });
    $('ao-gain').addEventListener('change', () => history.push());
    $('ao-join').addEventListener('input', () => {
        const v = parseInt($('ao-join').value, 10) / 1000;
        edit((c) => { c.join = v; }, { noHistory: true });
    });
    $('ao-join').addEventListener('change', () => history.push());
    $('ao-duck').addEventListener('input', () => {
        const v = parseInt($('ao-duck').value, 10);
        edit((c) => { c.duck = v; }, { noHistory: true });
    });
    $('ao-duck').addEventListener('change', () => history.push());
    const numField = (id, key) => $(id).addEventListener('change', () => {
        const v = Math.max(0, parseFloat($(id).value) || 0);
        edit((c) => { c[key] = v; });
    });
    numField('ao-gap-b', 'gapBefore');
    numField('ao-gap-a', 'gapAfter');
    numField('ao-fi', 'fadeIn');
    numField('ao-fo', 'fadeOut');
    // «Где» — общее поле времени (public/js/time-field.js): «1,20» — это 1:20, а не 1,2 с;
    // непонятное — отказ с объяснением; касание выделяет значение целиком.
    (window.__timeFieldQ = window.__timeFieldQ || []).push(() => {
        window.TimeField.bind($('ao-at'), {
            get: () => { const c = selected(); return c ? c.at : 0; },
            set: (v) => { edit((c) => { c.at = v; }); },
            min: 0,
            max: () => (model.main && model.main.buffer ? model.main.buffer.duration : Infinity),
            format: fmtMs, slug: CFG.slug || 'audio-overlay', field: 'at',
            onInvalid: (raw) => toast(String(L.timeUnreadable || '').replace(':value', raw), 'error'),
        });
    });
    document.querySelectorAll('#ao-clip-mode .tc-seg-b').forEach((b) => {
        b.addEventListener('click', () => {
            edit((c) => {
                c.mode = b.dataset.cmode === 'overlay' ? MODE_OVERLAY : MODE_INSERT;
                // Переключение режима меняет смысл ручек: у наложения нет стыка,
                // у вставки нет собственных фейдов. Даём им осмысленные значения
                // вместо нулей, оставшихся от другого режима.
                if (c.mode === MODE_OVERLAY) { c.join = 0; if (!c.fadeIn && !c.fadeOut) { c.fadeIn = 0.3; c.fadeOut = 0.5; } }
                else { c.fadeIn = 0; c.fadeOut = 0; c.duck = 0; if (!c.join) c.join = 0.12; }
            });
            $('ao-ws').dataset.clip = selected().mode;
            ae('feature_use', { slug: SLUG, f: 'clip_mode_changed', mode: selected().mode });
        });
    });
    $('ao-clip-del').addEventListener('click', () => {
        const c = selected();
        if (!c) return;
        history.push();
        model.clips = model.clips.filter((x) => x.id !== c.id);
        selectClip(null);
        replan();
    });
    $('ao-back').addEventListener('click', () => selectClip(null));
    $('ao-clip-play').addEventListener('click', async () => {
        const c = selected();
        if (!c) return;
        const p = planned.pieces.find((x) => x.clip.id === c.id);
        if (p) await userPlay('clip', Math.max(0, p.at - 1.5));
    });
    $('ao-match').addEventListener('click', async () => {
        const c = selected();
        if (!c || !model.main) return;
        const btn = $('ao-match');
        if (btn.getAttribute('aria-busy') === 'true') return;
        // Обычно число уже готово (меряли в фоне после загрузки); если нет —
        // кнопка ждёт, а страница отвечает: замер идёт порциями.
        btn.setAttribute('aria-busy', 'true');
        let r;
        try { r = await matchGain(model.main.buffer, c.buffer, LIMITS); }
        finally { btn.removeAttribute('aria-busy'); }
        if (selected() !== c) return;   // пока мерили, человек выбрал другой клип
        if (!r) { toast(L.matchFail || '', 'warning'); return; }
        history.push();
        c.gain = r.gain;
        replan();
        fillInspector(c);
        $('ao-clip-note').dataset.said = '1';
        $('ao-clip-note').textContent = (L.matched || '')
            .replace(':gain', Math.round(r.gain * 100) + '%')
            .replace(':lufs', r.mainLufs.toFixed(1));
        ae('feature_use', { slug: SLUG, f: 'loudness_matched', gain: Math.round(r.gain * 100) });
    });
    $('ao-snap').addEventListener('change', () => {
        timeline.showPauses($('ao-snap').checked);
        if (window.__toolSettings) window.__toolSettings.save(SLUG, 'snap', $('ao-snap').checked ? '1' : '0');
    });
    if (window.__toolSettings) {
        $('ao-snap').checked = window.__toolSettings.load(SLUG, 'snap', '1') !== '0';
    }

    /* выдача */
    fillFormats();
    $('ao-export').addEventListener('click', doExport);
    $('ao-cancel').addEventListener('click', () => { if (exportAbort) exportAbort.abort(); });

    /* пакет */
    $('ao-batch-d').addEventListener('toggle', () => { if ($('ao-batch-d').open) paintBatch(); });
    $('ao-batch-pick').addEventListener('click', () => $('ao-batch-input').click());
    $('ao-batch-input').addEventListener('change', () => {
        batchFiles = Array.from($('ao-batch-input').files || []).slice(0, CFG.batchCap || 1);
        $('ao-batch-input').value = '';
        paintBatch();
    });
    $('ao-batch-run').addEventListener('click', runBatch);

    /* клавиатура */
    /* Чья клавиша — решает общий примитив по РАБОТЕ клавиши в элементе фокуса,
       а не по тегу: у ползунка громкости пробела нет, у выбранного мышью списка
       формата и у кликнутой галочки — тоже (у дошедшего до них Tab-ом — есть).
       Прежнее «INPUT или SELECT — не трогаем» глушило пробел после любой ручки
       и переключало галочку вместо Play. Разбор — key-owned-by-focus.md. */
    const ownedByFocus = (ev) => {
        if (window.__keyOwnedByFocus) return window.__keyOwnedByFocus(ev);
        const tag = (ev.target && ev.target.tagName) || '';   // страница из кэша со старым ядром
        return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };
    document.addEventListener('keydown', (ev) => {
        if (!model.main) return;
        if (ownedByFocus(ev)) return;
        if (ev.code === 'Space') {
            ev.preventDefault();
            // Зажатый пробел шлёт повторы — это не «пауза-play-пауза» каждые 30 мс.
            if (!ev.repeat) userPlay('space');
        }
        else if (ev.key === 'Delete' && selected()) { ev.preventDefault(); $('ao-clip-del').click(); }
        else if ((ev.ctrlKey || ev.metaKey) && (window.__keyLetter ? window.__keyLetter(ev) : String(ev.key || '').toLowerCase()) === 'z') {
            ev.preventDefault();
            if (ev.shiftKey) { if (history.redo()) { selectClip(null); replan(); } }
            else if (history.undo()) { selectClip(null); replan(); }
        }
    });

    // Шкала — вход для сторожей: жесты по холсту иначе не воспроизвести
    // (координаты времени знает только она).
    window.__aoTimeline = timeline;

    /* остановка — ДО размера: она нужна на любом движке */
    window.addEventListener('pagehide', () => stopPlayback(false));
    document.addEventListener('visibilitychange', () => { if (document.hidden) stopPlayback(false); });

    /* размер. ResizeObserver есть не везде: Safari до 13.1 / iOS до 13.4 (здесь
       — iPad на iOS 12.5) бросал «Can't find variable: ResizeObserver», boot обрывался на
       последних строках, шкала не следовала за поворотом и шириной окна, а
       остановка выше (тогда стоявшая ниже) не навешивалась вовсе: звук играл
       в скрытой вкладке (Sentry TIMBRICA-FRONTEND-NS). Нет наблюдателя —
       следим за окном: шкала живёт в ширину страницы. */
    if (typeof ResizeObserver === 'function') {
        new ResizeObserver(() => timeline.resize()).observe(canvas.parentElement);
    } else {
        window.addEventListener('resize', () => timeline.resize());
    }
}

/* Отладочный зонд для стендов: отдаёт состояние, которого не видно на экране.
   Не UI — читается только тестами. */
window.__aoProbe = () => ({
    clips: model.clips.length,
    pauses: pauses.length,
    duration: planned ? +planned.duration.toFixed(2) : null,
    mainDur: model.main ? +model.main.buffer.duration.toFixed(2) : null,
    peaks: (() => {
        const p = timeline && timeline.state.peaks.get('main');
        if (!p) return null;
        let mx = 0;
        for (let i = 0; i < p.data.length; i++) mx = Math.max(mx, Math.abs(p.data[i]));
        return { buckets: p.buckets, maxAbs: +mx.toFixed(3) };
    })(),
    segments: planned ? planned.segments.length : 0,
    pieces: planned ? planned.pieces.map((x) => ({ id: x.clip.id, mode: x.clip.mode, at: +x.at.toFixed(2), len: +x.len.toFixed(2) })) : [],
});

/* Установить ручки ПЕРВОГО клипа — вход для сторожей. Через интерфейс те же
   значения задаются десятком кликов по ползункам, и тест мерил бы тогда
   ползунки, а не звук. */
window.__aoSet = (mode, at, knobs) => {
    const c = model.clips[0];
    if (!c) return false;
    c.mode = mode === 'overlay' ? MODE_OVERLAY : MODE_INSERT;
    if (at != null) c.at = at;
    Object.assign(c, knobs || {});
    replan();
    selectClip(c.id);
    return true;
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
